import type React from "react"
import { memo, useMemo, useState } from "react"
import { animated, config, useSpring } from "@react-spring/web"
import styles from "../../SideMenu.module.scss";
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { logOut } from "../../../stores/slices/current-user-slice"
import { ApplicationRoutes } from "../../../routes"
import BillsSideMenuButton, { type BillsSideMenuButtonProps } from "./BillsSideMenuButton"
import {
  CloseMobileMenuIcon,
  LogOutIcon
} from "../../../assets/icons"
import { MenuItemData, SubMenuData } from "../../menu.types";
import { MENU_STRUCTURE } from "../BillMenu.constant"
import { TenantLogo } from "../../../tenant/branding/TenantLogo"
import { useTenant } from "../../../tenant/branding/useTenant"

type Props = {
  isColapsed: boolean
  isDisabled: boolean
  mobileMenuOpen: boolean
  onMobileMenuClick: () => void
  onSubMenuInteraction: (mainMenuName: string, subMenuItems: SubMenuData[], isOpen: boolean, activeSubMenuItem?: string) => void
}

export const createSideMenuButtons = (
  currentPath: string,
  colapsed: boolean,
  isDisabled: boolean,
  executeWrapper?: (func: () => void) => void,
  onSubMenuInteraction?: (mainMenuName: string, subMenuItems: SubMenuData[], isOpen: boolean, activeSubMenuItem?: string) => void
): BillsSideMenuButtonProps[] => {

  let menuItems: MenuItemData[] = [];

  switch (true) {
    case currentPath.includes(ApplicationRoutes.ADMIN_BILLS):
      menuItems = MENU_STRUCTURE.bills;
      break;
    case currentPath.includes(ApplicationRoutes.SOLAR_MARKET):
      menuItems = MENU_STRUCTURE.marketplace;
      break;
    default:
      return [];
  }


  return menuItems.map((item) => ({
    text: item.text,
    icon: item.icon,
    isActiveIcon: item.isActiveIcon,
    route: item.route,
    disabled: item.disabled,
    executeWrapper,
    colapsed,
    isDisabled,
    subMenuItems: item.subMenuItems,
    onSubMenuInteraction
  }));
};

const BillsSideMenu: React.FC<Props> = memo((props: Props) => {
  const { isColapsed, isDisabled, mobileMenuOpen, onMobileMenuClick, onSubMenuInteraction } = props

  const location = useLocation()
  const dispatch = useDispatch()
  const navigator = useNavigate()
  const currentPath = location.pathname
  const { logo, name } = useTenant()

  const [currentOpenSubmenu, setCurrentOpenSubmenu] = useState<string | null>(null);

  const logout = () => {
    dispatch(logOut())
    navigator(`/${ApplicationRoutes.ADMIN_SECRET}`)
  }

  const handleSubMenuInteraction = (mainMenuName: string, subMenuItems: SubMenuData[], isOpen: boolean, activeSubMenuItem?: string) => {
    setCurrentOpenSubmenu(isOpen ? mainMenuName : null);

    if (onSubMenuInteraction) {
      onSubMenuInteraction(mainMenuName, subMenuItems, isOpen, activeSubMenuItem);
    }
  };

  const menuStyles = useSpring({
    from: {
      transform: mobileMenuOpen ? "translateX(-100%)" : "translateX(0%)",
    },
    to: {
      transform: mobileMenuOpen ? "translateX(0%)" : "translateX(-100%)",
    },
    config: config.gentle,
  })

  const executeWrapper = useMemo(
    () => (func: () => void) => {
      func()
      onMobileMenuClick()
    },
    [onMobileMenuClick],
  )

  const menuButtons = useMemo(() => {
    return createSideMenuButtons(
      currentPath,
      isColapsed,
      isDisabled,
      executeWrapper,
      handleSubMenuInteraction
    ).map(buttonProps => ({
      ...buttonProps,
      currentOpenSubmenu
    }));
  }, [currentPath, isColapsed, isDisabled, executeWrapper, currentOpenSubmenu]);

  const renderButtons = useMemo(() => {

    return menuButtons.map((button, index) => {
      const buttonWithWrapper = {
        ...button,
        executeWrapper,
      }

      return <BillsSideMenuButton onSubMenuInteraction={onSubMenuInteraction} key={button.text} {...buttonWithWrapper} />
    })
  }, [menuButtons])

  return (
    <>
      <div className={`${styles.container}`}>
        <div className={`${styles.logoContainer}`}>
          <a href={`/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.DASHBOARD}`}>
            <TenantLogo logo={logo} alt={name} className={styles.logo} />
          </a>
        </div>
        {renderButtons}
        <button title="Logout" className={`standardButton ${styles.logoutButton} ${styles.colapsed}`} onClick={logout}>
          <LogOutIcon />
        </button>
      </div>
      <animated.div style={menuStyles} className={styles.mobileMenuContainer}>
        <div className={styles.innerContainer}>
          <div className={styles.topbar}>
            <img className={styles.logo} src="../../../images/landing-page-header-icon.png" alt="placeholder" />
            <CloseMobileMenuIcon onClick={onMobileMenuClick} style={{ color: "#42577A" }} />
          </div>
          <div className={styles.buttonSection}>{renderButtons}</div>
          <div className={styles.logoutContainer}>
            <button className={styles.mobileLogout} onClick={logout}>
              <LogOutIcon />
              <span className={styles.text}>Logout</span>
            </button>
          </div>
        </div>
      </animated.div>
    </>
  )
})

export default BillsSideMenu
